import { Portal } from "radix-ui";
import { useEffect, useRef, useState } from "react";
import type { SuggestionState } from "~/features/documents/editor/suggestionTrigger";
import { Text } from "~/foundation/components/Text";

/**
 * The list a `/` or `@` opens — one component, because both menus are the same interaction:
 * a filtered list under the caret, driven entirely from the keyboard.
 *
 * Keyboard handling lives on `window` in the capture phase because the editor has focus the
 * whole time: the menu never takes it. That is what lets somebody type, arrow, and press Enter
 * without their caret ever moving out of the sentence they are writing.
 */

export interface SuggestionItem {
  id: string;
  title: string;
  /** A short right-hand hint — a section name, a thinker's description. */
  detail?: string;
  /** Rendered as a small heading above the first item of a run. */
  section?: string;
}

export function SuggestionMenu({
  state,
  items,
  emptyLabel,
  onSelect,
  onDismiss,
}: {
  state: SuggestionState;
  items: SuggestionItem[];
  emptyLabel: string;
  onSelect: (item: SuggestionItem) => void;
  onDismiss: () => void;
}) {
  const [index, setIndex] = useState(0);
  const listRef = useRef<HTMLDivElement | null>(null);

  // A changed query is a changed list; keeping the old highlight would select the wrong thing.
  useEffect(() => setIndex(0), [state.query]);

  useEffect(() => {
    if (!state.active || items.length === 0) {
      return undefined;
    }
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "ArrowDown") {
        event.preventDefault();
        setIndex((current) => (current + 1) % items.length);
        return;
      }
      if (event.key === "ArrowUp") {
        event.preventDefault();
        setIndex((current) => (current - 1 + items.length) % items.length);
        return;
      }
      if (event.key === "Enter" || event.key === "Tab") {
        const chosen = items[index];
        if (chosen) {
          event.preventDefault();
          onSelect(chosen);
        }
        return;
      }
      if (event.key === "Escape") {
        event.preventDefault();
        onDismiss();
      }
    };
    window.addEventListener("keydown", onKeyDown, true);
    return () => window.removeEventListener("keydown", onKeyDown, true);
  }, [index, items, onDismiss, onSelect, state.active]);

  // Keep the highlighted row in view when arrowing through a long list.
  useEffect(() => {
    listRef.current?.querySelector<HTMLElement>('[data-active="true"]')?.scrollIntoView({
      block: "nearest",
    });
  }, []);

  if (!state.active) {
    return null;
  }

  const top = state.rect ? state.rect.bottom + 6 : 120;
  const left = state.rect ? Math.max(8, state.rect.left) : 120;

  return (
    <Portal.Root>
      <div
        ref={listRef}
        role="listbox"
        aria-label={emptyLabel}
        style={{ position: "fixed", top, left, zIndex: 60, width: 280 }}
        className="max-h-64 overflow-y-auto rounded-md border border-divider bg-menu-bg p-1 shadow-menu"
      >
        {items.length === 0 ? (
          <Text variant="small" className="p-2 text-text-tertiary">
            {emptyLabel}
          </Text>
        ) : (
          items.map((item, position) => (
            <div key={item.id}>
              {item.section && items[position - 1]?.section !== item.section ? (
                <Text variant="small" className="px-3 pt-2 pb-1 text-text-tertiary">
                  {item.section}
                </Text>
              ) : null}
              <button
                type="button"
                data-active={position === index}
                // Mousedown, not click: a click would blur the editor first and the insertion
                // would land with no selection to replace.
                onMouseDown={(event) => {
                  event.preventDefault();
                  onSelect(item);
                }}
                onMouseEnter={() => setIndex(position)}
                className={`flex w-full items-center gap-2 rounded-sm px-3 py-1.5 text-start text-body ${
                  position === index ? "bg-menu-item-selected text-text" : "text-text"
                }`}
              >
                <span className="min-w-0 flex-1 truncate">{item.title}</span>
                {item.detail ? (
                  <span className="shrink-0 text-small text-text-tertiary">{item.detail}</span>
                ) : null}
              </button>
            </div>
          ))
        )}
      </div>
    </Portal.Root>
  );
}
